import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KundliPage = () => {
  const { i18n } = useTranslation();
  const isHindi = i18n.language === 'hi';
  const [form, setForm] = useState({ name: '', dob: '', time_of_birth: '', place_of_birth: '', gender: 'male' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await axios.post(`${API}/kundli/generate`, form, { timeout: 120000 });
      setResult(res.data);
    } catch (err) {
      if (err.code === 'ECONNABORTED') {
        setError(isHindi ? 'कुंडली बनने में समय लग रहा है। कृपया पुनः प्रयास करें।' : 'Kundli generation is taking longer than expected. Please try again.');
      } else {
        setError(err.response?.data?.detail || (isHindi ? 'कुंडली बनाने में त्रुटि। कृपया पुनः प्रयास करें।' : 'Error generating Kundli. Please try again.'));
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-24 px-6 md:px-12 lg:px-24 pb-16">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl sm:text-5xl font-serif text-[#F5F2EB] text-center mb-4" data-testid="kundli-heading">
          {isHindi ? 'मुफ्त कुंडली' : 'Free Kundli Generator'}
        </h1>
        <p className="text-[#A3A3A3] text-center mb-12">
          {isHindi ? 'अपने जन्म विवरण दर्ज करें और AI-संचालित कुंडली प्राप्त करें' : 'Enter your birth details and get AI-powered Kundli analysis'}
        </p>

        {!result ? (
          <form onSubmit={handleGenerate} className="bg-[#1A1A1A] border border-[#C9A84C]/15 p-8 max-w-xl mx-auto">
            {error && <div className="bg-red-500/10 border border-red-500/50 text-red-500 px-4 py-3 mb-4 text-sm">{error}</div>}
            <div className="space-y-5">
              <div>
                <label className="text-xs text-[#A3A3A3] uppercase tracking-wider block mb-1">{isHindi ? 'नाम' : 'Full Name'}</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-transparent border-b border-[#C9A84C]/30 py-3 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" required data-testid="kundli-name" />
              </div>
              <div>
                <label className="text-xs text-[#A3A3A3] uppercase tracking-wider block mb-1">{isHindi ? 'जन्म तिथि' : 'Date of Birth'}</label>
                <input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })}
                  className="w-full bg-transparent border-b border-[#C9A84C]/30 py-3 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" required data-testid="kundli-dob" />
              </div>
              <div>
                <label className="text-xs text-[#A3A3A3] uppercase tracking-wider block mb-1">{isHindi ? 'जन्म समय' : 'Time of Birth'}</label>
                <input type="time" value={form.time_of_birth} onChange={(e) => setForm({ ...form, time_of_birth: e.target.value })}
                  className="w-full bg-transparent border-b border-[#C9A84C]/30 py-3 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" required data-testid="kundli-time" />
              </div>
              <div>
                <label className="text-xs text-[#A3A3A3] uppercase tracking-wider block mb-1">{isHindi ? 'जन्म स्थान' : 'Place of Birth'}</label>
                <input value={form.place_of_birth} onChange={(e) => setForm({ ...form, place_of_birth: e.target.value })}
                  className="w-full bg-transparent border-b border-[#C9A84C]/30 py-3 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" placeholder={isHindi ? 'शहर, राज्य' : 'City, State'} required data-testid="kundli-place" />
              </div>
              <div>
                <label className="text-xs text-[#A3A3A3] uppercase tracking-wider block mb-1">{isHindi ? 'लिंग' : 'Gender'}</label>
                <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}
                  className="w-full bg-[#1A1A1A] border-b border-[#C9A84C]/30 py-3 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" data-testid="kundli-gender">
                  <option value="male">{isHindi ? 'पुरुष' : 'Male'}</option>
                  <option value="female">{isHindi ? 'महिला' : 'Female'}</option>
                  <option value="other">{isHindi ? 'अन्य' : 'Other'}</option>
                </select>
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="mt-6 w-full bg-[#C9A84C] text-[#0A0A0A] py-3 font-medium hover:bg-[#E5C870] transition-colors disabled:opacity-40 uppercase tracking-wider text-sm flex items-center justify-center gap-2"
              data-testid="generate-kundli-btn">
              {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> {isHindi ? 'AI कुंडली बना रही है... (30-60 सेकंड लग सकते हैं)' : 'AI generating Kundli... (may take 30-60 seconds)'}</> : (isHindi ? 'कुंडली बनाएं' : 'Generate Kundli')}
            </button>
          </form>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="bg-[#1A1A1A] border border-[#C9A84C]/15 p-8" data-testid="kundli-result">
              <div className="text-center mb-6">
                <h2 className="text-3xl font-serif text-[#C9A84C]">{result.name}</h2>
                <p className="text-[#A3A3A3]">{result.dob} | {result.time_of_birth} | {result.place_of_birth}</p>
              </div>
              <div className="prose prose-invert max-w-none">
                <div className="text-[#F5F2EB] whitespace-pre-wrap leading-relaxed text-sm">{result.kundli}</div>
              </div>
            </div>
            <button onClick={() => setResult(null)}
              className="mt-6 w-full bg-transparent border border-[#C9A84C]/50 text-[#C9A84C] py-3 font-medium hover:bg-[#C9A84C]/10 transition-colors uppercase tracking-wider text-sm">
              {isHindi ? 'नई कुंडली बनाएं' : 'Generate New Kundli'}
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default KundliPage;
